import React, {useCallback, useMemo, useState} from "react";
import {useParams, Link} from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Table,
  InputGroup,
  Modal,
  Form,
  Alert,
} from "react-bootstrap";
import {
  FaPaperPlane,
  FaEye,
  FaCheckCircle,
  FaArrowLeft,
  FaGift,
  FaReply,
  FaSave,
  FaSearch,
  FaFileExcel,
  FaComment, // Icono para iniciar chat
} from "react-icons/fa";
import {Doughnut} from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
} from "chart.js";
import * as XLSX from "xlsx";
import allMetrics from "../data/campaignMetrics.json"; // Importamos las métricas reales
import PollResults from "../components/PollResults"; // 1. Importamos el nuevo componente

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title
);

// Componente reutilizable para las tarjetas de KPIs
const KpiCard = ({icon, title, value, color}) => (
  <Card
    className={`shadow-sm rounded-4 border-0 border-start border-5 border-${color} h-100`}
  >
    <Card.Body className="d-flex align-items-center p-3">
      <div className={`fs-3 text-${color} me-3`}>{icon}</div>
      <div>
        <div className="fs-5 fw-bold">{value}</div>
        <div className="text-muted text-uppercase small">{title}</div>
      </div>
    </Card.Body>
  </Card>
);

// Componente para el ícono de estado en la tabla
const StatusIcon = ({status, color, text}) => (
  <span className={`text-${color}`} title={text}>
    <FaCheckCircle />
  </span>
);

const CommunicationDetail = ({communications, lists, contacts, incentives}) => {
  const {communicationId} = useParams();
  const communication = communications.find(
    (c) => c.id.toString() === communicationId
  );

  // Estados para los filtros de la tabla de destinatarios
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilters, setStatusFilters] = useState({
    received: false,
    read: false,
    incentive: false,
    replied: false,
  });
  const [showIncentiveModal, setShowIncentiveModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const getRecipientContacts = useCallback(
    (listIds) => {
      const memberIds = new Set();
      (listIds || []).forEach((listId) => {
        const list = lists.find((l) => l.id === listId);
        list?.members.forEach((memberId) => memberIds.add(memberId));
      });
      return contacts.filter((contact) => memberIds.has(contact.id));
    },
    [lists, contacts]
  );

  // Obtenemos las métricas reales desde el archivo JSON
  const metrics = allMetrics.find(
    (m) => m.campaignId.toString() === communicationId
  ) || {
    sent: 0,
    read: 0,
    failed: 0,
  };

  // Para la demo, simulamos los incentivos y respuestas basados en los leídos
  const simulatedMetrics = {
    incentive: Math.floor(metrics.read * 0.45), // 45% de los que leyeron usan incentivo
    replied: Math.floor(metrics.read * 0.21), // 21% de los que leyeron responden
  };

  // 2. Procesamos todos los comunicados con encuestas
  const pollsData = useMemo(() => {
    if (!communication?.comunicados) return [];

    return communication.comunicados
      .filter((comunicado) => comunicado.poll)
      .map((comunicado) => {
        // Simulación de resultados de la encuesta
        const totalVotes = Math.floor(metrics.read * 0.7); // 70% de los que leyeron, votan
        let votesRemaining = totalVotes;
        const results = comunicado.poll.options
          .map((option, index, arr) => {
            const isLast = index === arr.length - 1;
            const votes = isLast
              ? votesRemaining
              : Math.floor(Math.random() * votesRemaining);
            votesRemaining -= votes;
            return {option, votes};
          })
          .sort((a, b) => b.votes - a.votes); // Ordenar de más a menos votado

        return {
          comunicadoId: comunicado.id,
          comunicadoTitle: comunicado.title,
          poll: comunicado.poll,
          results,
        };
      });
  }, [communication, metrics.read]);

  // Obtenemos los destinatarios y les asignamos estados de forma aleatoria pero consistente
  const recipientsWithStatus = useMemo(() => {
    const recipients = communication
      ? getRecipientContacts(communication.sentToListIds)
      : [];
    if (!recipients.length) return [];

    // 1. Asignamos aleatoriamente quiénes leyeron
    const readers = new Set(
      [...recipients]
        .sort(() => 0.5 - Math.random())
        .slice(0, metrics.read)
        .map((c) => c.id)
    );

    // 2. De los que leyeron, asignamos aleatoriamente quiénes usaron el incentivo
    const incentiveUsers = new Set(
      [...Array.from(readers)]
        .sort(() => 0.5 - Math.random())
        .slice(0, simulatedMetrics.incentive)
    );

    // 3. De los que usaron incentivo, asignamos aleatoriamente quiénes respondieron
    const repliers = new Set(
      [...Array.from(incentiveUsers)]
        .sort(() => 0.5 - Math.random())
        .slice(0, simulatedMetrics.replied)
    );

    // Asignamos estados basados en las métricas
    return recipients.map((contact) => {
      const status = {
        received: communication?.status === "Enviada",
        read: readers.has(contact.id),
        incentive: incentiveUsers.has(contact.id),
        replied: repliers.has(contact.id),
        incentiveCode: (() => {
          if (!incentiveUsers.has(contact.id) || !communication.incentiveId)
            return null;
          const incentive = incentives.find(
            (i) => i.id === communication.incentiveId
          );
          const codePrefix = incentive ? incentive.codePrefix : "DESC";
          return `${codePrefix}-${contact.id}${communicationId}`;
        })(),
      };
      return {...contact, status};
    });
  }, [
    communication,
    metrics.read,
    simulatedMetrics.incentive,
    simulatedMetrics.replied,
    communicationId,
    incentives,
    getRecipientContacts,
  ]);

  // Lógica para filtrar la tabla de destinatarios
  const filteredRecipients = useMemo(() => {
    return recipientsWithStatus.filter((contact) => {
      // Filtro por texto
      const searchMatch =
        !searchTerm ||
        `${contact.firstName} ${contact.lastName} ${contact.transportista} ${contact.phone}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      // Filtro por estados (solo si algún filtro de estado está activo)
      const activeStatusFilters = Object.values(statusFilters).some((v) => v);
      if (!activeStatusFilters) {
        return searchMatch;
      }

      const statusMatch =
        (!statusFilters.received || contact.status.received) &&
        (!statusFilters.read || contact.status.read) &&
        (!statusFilters.incentive || contact.status.incentive) &&
        (!statusFilters.replied || contact.status.replied);

      return searchMatch && statusMatch;
    });
  }, [recipientsWithStatus, searchTerm, statusFilters]);

  const handleStatusFilterChange = (filterName) => {
    setStatusFilters((prev) => ({...prev, [filterName]: !prev[filterName]}));
  };

  const handleExportRecipients = () => {
    const dataToExport = filteredRecipients.map((contact) => ({
      Nombre: `${contact.firstName} ${contact.lastName}`,
      Empresa: contact.transportista,
      Teléfono: contact.phone,
      Recibido: contact.status.received ? "Sí" : "No",
      Leído: contact.status.read ? "Sí" : "No",
      Incentivo: contact.status.incentive ? "Sí" : "No",
      Respuesta: contact.status.replied ? "Sí" : "No",
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Destinatarios");
    XLSX.writeFile(workbook, `Destinatarios_${communication.name}.xlsx`);
  };

  const handleRowClick = (contact) => {
    if (contact.status.incentive) {
      setSelectedContact(contact);
      setShowIncentiveModal(true);
    }
  };

  const handleCloseIncentiveModal = () => {
    setShowIncentiveModal(false);
  };
  if (!communication) {
    return (
      <Container className="text-center mt-5">
        <h2>Comunicación no encontrada</h2>
        <p>La comunicación que buscas no existe o fue eliminada.</p>
        <Button as={Link} to="/communications">
          Volver al Comunicaciones
        </Button>
      </Container>
    );
  }

  const doughnutData = {
    labels: ["Leídos", "Solo Recibidos"],
    datasets: [
      {
        data: [metrics.read, metrics.sent - metrics.read],
        backgroundColor: ["#22c55e", "#e2e8f0"],
        borderColor: ["#ffffff", "#ffffff"],
        borderWidth: 2,
      },
    ],
  };

  const incentiveDoughnutData = {
    labels: ["Usaron Incentivo", "Leído sin Incentivo"],
    datasets: [
      {
        data: [
          simulatedMetrics.incentive,
          metrics.read - simulatedMetrics.incentive,
        ],
        backgroundColor: ["#3b82f6", "#a5b4fc"], // Azul para incentivos, un tono más claro para el resto
        borderColor: ["#ffffff", "#ffffff"],
        borderWidth: 2,
      },
    ],
  };

  const statusMap = {
    Enviada: (
      <Badge
        bg="success-subtle"
        text="success-emphasis"
        className="fw-semibold"
      >
        <FaCheckCircle className="me-1" /> Enviada
      </Badge>
    ),
    Borrador: (
      <Badge
        bg="secondary-subtle"
        text="secondary-emphasis"
        className="fw-semibold"
      >
        <FaSave className="me-1" /> Borrador
      </Badge>
    ),
  };

  return (
    <Container fluid className="p-4 bg-light">
      <Row className="align-items-center mb-4 justify-content-between">
        <Col xs="auto" className="me-auto">
          <Button as={Link} to="/communications" variant="light">
            <FaArrowLeft className="me-2" />
            Volver al Comunicaciones
          </Button>
        </Col>
        <Col xs="auto">
          <p className="text-muted mb-0">
            {statusMap[communication.status] || communication.status}
            {communication.sentDate &&
              ` • Enviada el ${new Date(
                communication.sentDate
              ).toLocaleDateString("es-CL")}`}
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={5} xl={4}>
          <Card className="shadow-sm rounded-4 border-0 h-100 d-flex flex-column">
            <Card.Header className="bg-white p-3 border-bottom-0">
              <h5 className="mb-0 fw-bold">Rendimiento General</h5>
            </Card.Header>
            <Card.Body>
              <Row className="text-center">
                <Col>
                  <div style={{width: "80px", margin: "0 auto"}}>
                    <Doughnut
                      data={doughnutData}
                      options={{plugins: {legend: {display: false}}}}
                    />
                  </div>
                  <p className="mt-2 mb-0 fw-bold">Lectura</p>
                </Col>
                <Col>
                  <div style={{width: "80px", margin: "0 auto"}}>
                    <Doughnut
                      data={incentiveDoughnutData}
                      options={{plugins: {legend: {display: false}}}}
                    />
                  </div>
                  <p className="mt-2 mb-0 fw-bold">Incentivos</p>
                </Col>
              </Row>
              <div className="d-flex justify-content-center mt-3 small text-muted">
                <span className="me-3">
                  <span
                    className="d-inline-block rounded-circle me-2"
                    style={{
                      width: "10px",
                      height: "10px",
                      backgroundColor: "#22c55e",
                    }}
                  ></span>
                  Leído
                </span>
                <span className="me-3">
                  <span
                    className="d-inline-block rounded-circle me-2"
                    style={{
                      width: "10px",
                      height: "10px",
                      backgroundColor: "#3b82f6",
                    }}
                  ></span>
                  Incentivo
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={7} xl={8}>
          <Row>
            <Col md={6} className="mb-3">
              <KpiCard
                icon={<FaPaperPlane />}
                title="Enviados"
                value={metrics.sent || 0}
                color="primary"
              />
            </Col>
            <Col md={6} className="mb-3">
              <KpiCard
                icon={<FaEye />}
                title="Tasa de Lectura"
                value={`${
                  metrics.sent > 0
                    ? Math.round((metrics.read / metrics.sent) * 100)
                    : 0
                }%`}
                color="success"
              />
            </Col>
            <Col md={6} className="mb-3 mb-md-0">
              <KpiCard
                icon={<FaGift />}
                title="Incentivos Usados"
                value={simulatedMetrics.incentive}
                color="info"
              />
            </Col>
            <Col md={6}>
              <KpiCard
                icon={<FaReply />}
                title="Respuestas"
                value={simulatedMetrics.replied}
                color="warning"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col lg={8}>
          <Card className="shadow-sm rounded-4 border-0 mb-4">
            <Card.Header className="bg-white p-3 border-bottom-0 d-flex flex-wrap justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold me-3">
                Estado de Destinatarios ({filteredRecipients.length})
              </h5>
              <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                <InputGroup size="sm" style={{width: "200px"}}>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
                <Button
                  variant={statusFilters.read ? "success" : "outline-success"}
                  size="sm"
                  onClick={() => handleStatusFilterChange("read")}
                >
                  Leído
                </Button>
                <Button
                  variant={statusFilters.incentive ? "info" : "outline-info"}
                  size="sm"
                  onClick={() => handleStatusFilterChange("incentive")}
                >
                  Incentivo
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExportRecipients}
                  title="Exportar a Excel"
                >
                  <FaFileExcel />
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{maxHeight: "450px", overflowY: "auto"}}>
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Empresa</th>
                      <th className="text-center">Recibido</th>
                      <th className="text-center">Leído</th>
                      <th className="text-center">Incentivo</th>
                      <th className="text-center">Respuesta</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecipients.map((contact) => (
                      <tr
                        key={contact.id}
                        onClick={() => handleRowClick(contact)}
                        style={{
                          cursor: contact.status.incentive
                            ? "pointer"
                            : "default",
                        }}
                        className={
                          contact.status.incentive ? "table-row-hover" : ""
                        }
                      >
                        <td>
                          <div className="fw-bold">
                            {contact.firstName} {contact.lastName}
                          </div>
                          <div className="text-muted small">
                            {contact.phone}
                          </div>
                        </td>
                        <td>{contact.transportista}</td>
                        <td className="text-center">
                          {contact.status.received && (
                            <StatusIcon color="primary" text="Recibido" />
                          )}
                        </td>
                        <td className="text-center">
                          {contact.status.read && (
                            <StatusIcon color="success" text="Leído" />
                          )}
                        </td>
                        <td className="text-center">
                          {contact.status.incentive && (
                            <StatusIcon color="info" text="Incentivo Usado" />
                          )}
                        </td>
                        <td className="text-center">
                          {contact.status.replied && (
                            <StatusIcon color="warning" text="Ha respondido" />
                          )}
                        </td>
                        <td className="text-center">
                          <Button
                            as={Link}
                            to="/inbox"
                            state={{contactId: contact.id}} // Pasamos el ID del contacto
                            variant="outline-success"
                            size="sm"
                            title={`Iniciar chat con ${contact.firstName}`}
                          >
                            <FaComment />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          {/* 3. Mostramos los resultados de todas las encuestas en la columna lateral */}
          {pollsData.length > 0 &&
            pollsData.map((pollData, index) => (
              <div
                key={pollData.comunicadoId}
                className={index > 0 ? "mt-4" : ""}
              >
                <PollResults
                  poll={pollData.poll}
                  results={pollData.results}
                  communicationId={communication.id}
                  comunicadoTitle={pollData.comunicadoTitle}
                />
              </div>
            ))}
          {pollsData.length === 0 && (
            <Card className="shadow-sm rounded-4 border-0">
              <Card.Body className="text-center text-muted p-4 d-flex flex-column justify-content-center align-items-center h-100">
                <h6 className="fw-bold">Sin Encuestas Activas</h6>
                <p className="small mb-0">
                  Esta comunicación no incluye encuestas para mostrar.
                </p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Modal para ver el detalle del incentivo */}
      {selectedContact && (
        <Modal
          show={showIncentiveModal}
          onHide={handleCloseIncentiveModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaGift className="me-2 text-info" />
              Detalle del Incentivo
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Incentivo solicitado por:{" "}
              <strong>
                {selectedContact.firstName} {selectedContact.lastName}
              </strong>
            </p>
            <p>
              Comunicación: <strong>{communication.name}</strong>
            </p>
            <Alert variant="success" className="text-center">
              <Alert.Heading>Código de Descuento</Alert.Heading>
              <p className="fs-4 fw-bold mb-0">
                {selectedContact.status.incentiveCode}
              </p>
            </Alert>
          </Modal.Body>
        </Modal>
      )}
    </Container>
  );
};

export default CommunicationDetail;
